import React, { useState, useEffect } from 'react';
import { FiThermometer, FiInfo } from 'react-icons/fi';
import classNames from 'classnames';
import CircularSlider from 'react-circular-slider-svg';
import { AuxTypeCode } from '@/types/device';
import { AuxAssign, EquipmentItem, TemperatureInfo } from '@/types/dashboard';
import { apiService } from '@/utils/api';
import { useSelector ,useDispatch} from 'react-redux';
import { RootState } from '@/store';
import { ClipLoader } from 'react-spinners';
import { SunIcon, WaveIcon } from '@/assets/svg';
import { setLoading } from '@/store/slices/appSlice';


// Custom SVG icons moved to assets/svg



interface TemperatureResponse {
  temperatureInfo: {
    temperature: TemperatureInfo[];
  };
}

interface TemperatureSidebarProps {
  auxAssign?: AuxAssign;
}

// Section header component
const SectionHeader: React.FC<{
  title: string;
  icon: React.ReactNode;
  isActive?: boolean;
}> = ({ title, icon, isActive = true }) => (
  <h3 className={classNames(
    "flex items-center font-medium mb-4",
    {
      "text-gray-800 dark:text-gray-100": isActive,
      "text-gray-400 dark:text-gray-500": !isActive
    }
  )}>
    <span className={classNames("mr-2", {
      "text-blue-500 dark:text-blue-400": isActive,
      "text-gray-400 dark:text-gray-500": !isActive
    })}>
      {icon}
    </span>
    {title}
  </h3>
);

// Info box component
const InfoBox: React.FC<{
  text: string;
  isActive?: boolean;
}> = ({ text, isActive = true }) => (
  <div className={classNames(
    "mt-4 p-3 rounded text-sm",
    {
      "bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300": isActive,
      "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400": !isActive
    }
  )}>
    <div className="flex items-start">
      <FiInfo className="mt-0.5 mr-2 flex-shrink-0" />
      <p>{text}</p>
    </div>
  </div>
);

// Temperature control component
const TemperatureControl: React.FC<{
  currentTemp: number;
  targetTemp: number;
  onTargetChange: (value: number, options?: { commit?: boolean }) => void;
  isActive?: boolean;
}> = ({ currentTemp, targetTemp, onTargetChange, isActive = true }) => {
  const [localTarget, setLocalTarget] = useState(targetTemp);
  const [debounceTimer, setDebounceTimer] = useState<number | null>(null);
  
  const handleIncrease = () => {
    if (localTarget < 40) {
      const newValue = localTarget + 1;
      setLocalTarget(newValue);
      // 点击加号：立即提交（commit）
      onTargetChange(newValue, { commit: true });
    }
  };
  
  const handleDecrease = () => {
    if (localTarget > 0) {
      const newValue = localTarget - 1;
      setLocalTarget(newValue);
      // 点击减号：立即提交（commit）
      onTargetChange(newValue, { commit: true });
    }
  };

  // Format temperature for display
  const formatTemp = (temp: number) => {
    return temp / 10;
  };

  // Calculate percentage for circular progress
  const percentage = Math.min(100, Math.max(0, (localTarget / 40) * 100));
  
  // Determine color based on temperature
  const getColor = (temp: number) => {
    if (temp < 20) return '#3B82F6'; // cool - blue
    if (temp < 30) return '#10B981'; // moderate - green
    return '#F59E0B'; // warm - amber
  };
  
  const color = getColor(localTarget);

  return (
    <div className={classNames(
      "flex flex-col items-center mt-4",
      { "opacity-50": !isActive }
    )}>
      <div className="w-48 h-48 relative">
        <div
          className={classNames({ 'pointer-events-none opacity-50': !isActive })}
  
        >
          <CircularSlider
            size={192}
            trackWidth={12}
            minValue={0}
            maxValue={40}
            startAngle={40}
            endAngle={320}
            angleType={{ direction: 'cw', axis: '-y' }}
            handle1={{
              value: localTarget,
              onChange: (v: number) => {
                if (!isActive) return;
                const newValue = Math.round(Math.min(40, Math.max(0, v)));
                setLocalTarget(newValue);
                // 滑动中：不立即提交，触发去抖以在滑动结束后提交
                onTargetChange(newValue, { commit: false });
                // 以去抖防止频繁提交，同时在滑动结束触发最终提交
                if (debounceTimer) {
                  clearTimeout(debounceTimer);
                }
                const timer = window.setTimeout(() => {
                  onTargetChange(newValue, { commit: true });
                }, 400);
                setDebounceTimer(timer);
              },
            }}
            arcColor={color}
            arcBackgroundColor="#E5E7EB"
          />
        </div>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <span className="text-4xl font-bold" style={{ color }}>
            {localTarget}°C
          </span>
        </div>
      </div>
      
      <div className="flex items-center justify-between w-full mt-4">
        <button
          onClick={handleDecrease}
          disabled={!isActive || localTarget <= 0}
          className={classNames(
            "w-12 h-12 rounded-full flex items-center justify-center text-2xl",
            {
              "bg-blue-500 text-white hover:bg-blue-600": isActive && localTarget > 0,
              "bg-gray-200 text-gray-400 cursor-not-allowed": !isActive || localTarget <= 0
            }
          )}
        >
          -
        </button>
        <span className="text-lg font-medium text-gray-700 dark:text-gray-300">
          Set Pool Temp
        </span>
        <button
          onClick={handleIncrease}
          disabled={!isActive || localTarget >= 40}
          className={classNames(
            "w-12 h-12 rounded-full flex items-center justify-center text-2xl",
            {
              "bg-blue-500 text-white hover:bg-blue-600": isActive && localTarget < 40,
              "bg-gray-200 text-gray-400 cursor-not-allowed": !isActive || localTarget >= 40
            }
          )}
        >
          +
        </button>
      </div>
    </div>
  );
};

// Heater toggle component
const HeaterToggle: React.FC<{
  label: string;
  isSelected: boolean;
  isOn: boolean;
  onToggle: (enabled: boolean) => void;
  isActive?: boolean;
  icon?: React.ReactNode;
}> = ({ label, isSelected, isOn, onToggle, isActive = true }) => {
  if (!isActive) return null;
  return (
    <div className="flex items-center justify-between py-2">
      <div className="flex items-center space-x-2">
        {/* Unified circle icon, color indicates on/off */}
        <span
          className={classNames(
            "inline-block h-3 w-3 rounded-full",
            {
              "bg-green-500": isOn,
              "bg-gray-400 dark:bg-gray-600": !isOn
            }
          )}
        />
        <span className="text-sm text-gray-600 dark:text-gray-300">
          {label}
        </span>
      </div>
      <input
        type="checkbox"
        checked={isSelected}
        onChange={(e) => onToggle(e.target.checked)}
        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        aria-label={`${label} selected`}
      />
    </div>
  );
};

// Temperature summary section (Solar + Water at top)
const TemperatureSummary: React.FC<{
  poolTemp?: TemperatureInfo;
  spaTemp?: TemperatureInfo;
  hasSolarHeater: boolean;
}> = ({ poolTemp, spaTemp, hasSolarHeater }) => {
  if (!poolTemp && !spaTemp) return null;

  const waterRaw = poolTemp
    ? poolTemp.water_temp
    : spaTemp
    ? spaTemp.water_temp
    : 0;
  const waterDisplay = (waterRaw / 10).toFixed(1);

  const solarRaw = poolTemp
    ? poolTemp.solar_temp
    : spaTemp
    ? spaTemp.solar_temp
    : 0;
  const solarDisplay = hasSolarHeater ? (solarRaw / 10).toFixed(1) : null;

  return (
    <div className="p-4 mb-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
      <h3 className="text-lg font-medium text-gray-800 dark:text-gray-100 mb-4">Temperature Summary</h3>
      <div className="grid grid-cols-2 gap-4">
        {hasSolarHeater && (
          <div className="flex items-center p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20">
            <div className="p-2 rounded-full bg-amber-100 dark:bg-amber-800 mr-3">
              <SunIcon size={24} className="" />
            </div>
            <div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Solar Temperature</div>
              <div className="font-bold text-lg text-gray-800 dark:text-gray-100">{solarDisplay}°C</div>
            </div>
          </div>
        )}
        <div className="flex items-center p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20">
          <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-800 mr-3">
            <WaveIcon size={24} className="" />
          </div>
          <div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Water Temperature</div>
            <div className="font-bold text-lg text-gray-800 dark:text-gray-100">{waterDisplay}°C</div>
          </div>
        </div>
      </div>
    </div>
  );
};

const TemperatureSidebar: React.FC<TemperatureSidebarProps> = ({
  auxAssign
}) => {
  const { selectedDeviceId } = useSelector((state: RootState) => state.device);
  const [temperatureInfo, setTemperatureInfo] = useState<TemperatureResponse | null>(null);
  const [loading, setLoadingLocal] = useState<boolean>(true);
  const dispatch = useDispatch();

  // Check if heaters are available based on auxAssign
  const isCombinedHeaterDevice = auxAssign?.equipment?.some((aux: EquipmentItem) => aux.type_code === AuxTypeCode.COMBINE_POOL_SPA_HEATER) || false;
  const hasPoolHeater = isCombinedHeaterDevice || (auxAssign?.equipment?.some((aux: EquipmentItem) => aux.type_code === AuxTypeCode.POOL_HEATER) || false);
  const hasSpaHeater = auxAssign?.equipment?.some((aux: EquipmentItem) => aux.type_code === AuxTypeCode.SPA_HEATER) || false;
  const hasSolarHeater = isCombinedHeaterDevice || (auxAssign?.equipment?.some((aux: EquipmentItem) => 
    aux.type_code === AuxTypeCode.SOLAR_DEDICATED || aux.type_code === AuxTypeCode.SOLAR_FILTER_PUMP
  ) || false);

  // Fetch temperature data
  useEffect(() => {
    const fetchTemperatureInfo = async () => {
      if (!selectedDeviceId) return;
      
      setLoadingLocal(true);
      try {
        const data = await apiService.get<TemperatureResponse>(`/api/v1/iot-shadow/device/${selectedDeviceId}/temperatureInfo`);
        setTemperatureInfo(data);
      } catch (err) {
        console.error('Error fetching temperature info:', err);
      } finally {
        setLoadingLocal(false);
      }
    };

    fetchTemperatureInfo();
  }, [selectedDeviceId]);

  // Find pool and spa temperature info
  const poolTemp = temperatureInfo?.temperatureInfo?.temperature?.find(t => t.category === 'pool');
  const spaTemp = temperatureInfo?.temperatureInfo?.temperature?.find(t => t.category === 'spa');

  // Handle temperature change
  const postSetTemperatureInfo = async (payload: {
    desire_pool_temp: number;
    desire_spa_temp: number;
    is_solar_heater_selected: boolean;
    is_pool_heater_selected: boolean;
    is_spa_heater_selected: boolean;
  }) => {
    if (!selectedDeviceId) return;
    dispatch(setLoading(true));
    
    try {
      await apiService.post(`/api/v1/iot-shadow/device/${selectedDeviceId}/setTemperatureInfo`, payload);
      
      // Refresh temperature data after change
      await new Promise((resolve) => setTimeout(resolve, 300));
      const data = await apiService.get<TemperatureResponse>(`/api/v1/iot-shadow/device/${selectedDeviceId}/temperatureInfo`);
      setTemperatureInfo(data);
    } catch (err) {
      console.error('Error setting temperature info:', err);
    } finally {
      dispatch(setLoading(false));
    }
  };

  // Handle heater toggle -> 使用 setTemperatureInfo，其他值保持当前，仅更改对应 selected
  const handleHeaterToggle = async (category: string, heaterType: string, enabled: boolean) => {
    const pool = poolTemp;
    const spa = spaTemp;
    const desire_pool_temp = pool?.target_temp ?? 0;
    const desire_spa_temp = spa?.target_temp ?? 0;

    let is_solar_heater_selected = pool?.is_solar_heater_selected ?? spa?.is_solar_heater_selected ?? false;
    let is_pool_heater_selected = pool?.is_pool_heater_selected ?? false;
    let is_spa_heater_selected = spa?.is_spa_heater_selected ?? false;

    if (heaterType === 'solar_heater') {
      is_solar_heater_selected = enabled;
    } else if (heaterType === 'pool_heater') {
      is_pool_heater_selected = enabled;
    } else if (heaterType === 'spa_heater') {
      is_spa_heater_selected = enabled;
    }

    // 乐观更新：先更新 UI，再调用接口
    setTemperatureInfo(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        temperatureInfo: {
          temperature: prev.temperatureInfo.temperature.map(t => {
            if (category === 'pool' && t.category === 'pool') {
              if (heaterType === 'solar_heater') return { ...t, is_solar_heater_selected: enabled };
              if (heaterType === 'pool_heater') return { ...t, is_pool_heater_selected: enabled };
              return t;
            }
            if (category === 'spa' && t.category === 'spa') {
              if (heaterType === 'solar_heater') return { ...t, is_solar_heater_selected: enabled };
              if (heaterType === 'spa_heater') return { ...t, is_spa_heater_selected: enabled };
              return t;
            }
            return t;
          })
        }
      };
    });

    await postSetTemperatureInfo({
      desire_pool_temp,
      desire_spa_temp,
      is_solar_heater_selected,
      is_pool_heater_selected,
      is_spa_heater_selected,
    });
  };

  // Handle temperature change（滑块或加减触发）
  const handleTemperatureChange = async (category: string, temperature: number, options?: { commit?: boolean }) => {
    // 仅在 commit 时提交；滑动中只更新本地/父状态
    if (!options?.commit) return;

    const pool = poolTemp;
    const spa = spaTemp;
    let desire_pool_temp = pool?.target_temp ?? 0;
    let desire_spa_temp = spa?.target_temp ?? 0;

    if (category === 'pool') {
      desire_pool_temp = Math.max(0, Math.min(40, temperature));
    } else if (category === 'spa') {
      desire_spa_temp = Math.max(0, Math.min(40, temperature));
    }

    const payload = {
      desire_pool_temp,
      desire_spa_temp,
      is_solar_heater_selected: pool?.is_solar_heater_selected ?? spa?.is_solar_heater_selected ?? false,
      is_pool_heater_selected: pool?.is_pool_heater_selected ?? false,
      is_spa_heater_selected: spa?.is_spa_heater_selected ?? false,
    };

    await postSetTemperatureInfo(payload);
  };

  return (
    <div className="space-y-6">
      {/* Loading state */}
      {loading && (
        <div className="flex flex-col items-center justify-center h-64 p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <ClipLoader color="#3B82F6" size={40} />
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading temperature data...</p>
        </div>
      )}
      
      {/* Error state */}
      {/* {error && !loading && (
        <div className="flex flex-col items-center justify-center h-64 p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <FiInfo className="text-4xl mb-2 text-red-500" />
          <p className="text-red-500">{error}</p>
          <button 
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
            onClick={() => {
              setLoading(true);
              apiService.get<TemperatureResponse>(`/api/v1/iot-shadow/device/${selectedDeviceId}/temperatureInfo`)
                .then(data => {
                  setTemperatureInfo(data);
                  setError(null);
                })
                .catch(err => {
                  console.error('Error fetching temperature info:', err);
                  setError('Failed to load temperature data');
                })
                .finally(() => setLoading(false));
            }}
          >
            Retry
          </button>
        </div>
      )} */}
      
      {/* Temperature Summary Section */}
      {!loading && (poolTemp || spaTemp) && (
        <TemperatureSummary poolTemp={poolTemp} spaTemp={spaTemp} hasSolarHeater={hasSolarHeater} />
      )}
      
      {/* Pool Temperature Section */}
      {!loading && poolTemp && (
        <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <SectionHeader 
            title="Pool Temperature" 
            icon={<FiThermometer size={18} />}
          />
          {/* Heater Controls */}
          <div className="mb-6 space-y-2">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Heater Controls</h4>
            
            {hasPoolHeater && (
              <HeaterToggle
                label="Pool Heater"
                isSelected={poolTemp.is_pool_heater_selected}
                isOn={poolTemp.is_pool_heater_on}
                onToggle={(enabled) => handleHeaterToggle('pool', 'pool_heater', enabled)}
                isActive={hasPoolHeater}
                icon={<FiThermometer size={16} />}
              />
            )}
            
            {hasSolarHeater && (
              <HeaterToggle
                label="Solar Heater"
                isSelected={poolTemp.is_solar_heater_selected}
                isOn={poolTemp.is_solar_heater_on}
                onToggle={(enabled) => handleHeaterToggle('pool', 'solar_heater', enabled)}
                isActive={hasSolarHeater}
                icon={<SunIcon size={16} />}
              />
            )}
          </div>

          {/* Temperature Control */}
          <TemperatureControl
            currentTemp={poolTemp.water_temp}
            targetTemp={poolTemp.target_temp}
            onTargetChange={(temp, options) => handleTemperatureChange('pool', temp, options)}
          />

          <InfoBox 
            text="Pool temperature control allows you to set your desired water temperature and manage heating options."
          />
        </div>
      )}

      {/* Spa Temperature Section */}
      {!loading && spaTemp && (
        <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <SectionHeader 
            title="Spa Temperature" 
            icon={<FiThermometer size={18} />}
          />
          {/* Heater Controls */}
          <div className="mb-6 space-y-2">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Heater Controls</h4>
            
            {hasSpaHeater && (
              <HeaterToggle
                label="Spa Heater"
                isSelected={spaTemp.is_spa_heater_selected}
                isOn={spaTemp.is_spa_heater_on}
                onToggle={(enabled) => handleHeaterToggle('spa', 'spa_heater', enabled)}
                isActive={hasSpaHeater}
                icon={<FiThermometer size={16} />}
              />
            )}
            
            {hasSolarHeater && (
              <HeaterToggle
                label="Solar Heater"
                isSelected={spaTemp.is_solar_heater_selected}
                isOn={spaTemp.is_solar_heater_on}
                onToggle={(enabled) => handleHeaterToggle('spa', 'solar_heater', enabled)}
                isActive={hasSolarHeater}
                icon={<SunIcon size={16} />}
              />
            )}
          </div>

          {/* Temperature Control */}
          <TemperatureControl
            currentTemp={spaTemp.water_temp}
            targetTemp={spaTemp.target_temp}
            onTargetChange={(temp, options) => handleTemperatureChange('spa', temp, options)}
          />

          <InfoBox 
            text="Spa temperature control allows you to set your desired water temperature and manage heating options."
          />
        </div>
      )}
      
      {/* No temperature data message */}
      {!loading && !poolTemp && !spaTemp && (
        <div className="flex flex-col items-center justify-center h-64 p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-500">
          <FiInfo className="text-4xl mb-2" />
          <p>No temperature data available</p>
        </div>
      )}
    </div>
  );
};

export default TemperatureSidebar;